import React, { useState, useEffect } from 'react';
import { Col, Container, Row, Card, CardBody, CardHeader, Button, Badge, Alert } from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { useParams } from 'react-router-dom';
import { APIClient } from '../../../helpers/api_helper';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface ApiKey {
    id: number;
    projectId: number;
    envId: number;
    apiKey: string;
    expireDate: string | null;
    status: number;
    createdAt: string;
}

const ProjectApiKeys = () => {
    const { projectId } = useParams();
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [rotating, setRotating] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        fetchApiKeys();
    }, [projectId]);

    const fetchApiKeys = async () => {
        try {
            setLoading(true);
            const apiClient = new APIClient();
            const response: any = await apiClient.get(`/user/api-keys/project/${projectId}`);

            if (response.status === 200 && response.data) {
                setApiKeys(response.data);
            }
        } catch (error: any) {
            console.error('Error fetching API keys:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch API keys');
        } finally {
            setLoading(false);
        }
    };

    const handleRotateKey = async (envId: number, envName: string) => {
        if (!window.confirm(`Are you sure you want to rotate the ${envName} API key? The old key will remain valid for 15 days.`)) {
            return;
        }

        try {
            setRotating({ ...rotating, [envId]: true });
            const apiClient = new APIClient();
            const response: any = await apiClient.create('/user/api-keys/rotate', {
                projectId: parseInt(projectId || '0'),
                envId: envId
            });

            if (response.status === 200) {
                toast.success(`${envName} API key rotated successfully! Old key will expire in 15 days.`);
                fetchApiKeys(); // Refresh the list
            }
        } catch (error: any) {
            console.error('Error rotating API key:', error);
            toast.error(error.response?.data?.message || 'Failed to rotate API key');
        } finally {
            setRotating({ ...rotating, [envId]: false });
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('API key copied to clipboard!');
    };

    const getEnvironmentName = (envId: number) => {
        return envId === 1 ? 'Sandbox' : 'Production';
    };

    const getEnvironmentColor = (envId: number) => {
        return envId === 1 ? 'warning' : 'success';
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const groupKeysByEnvironment = () => {
        const sandboxKeys = apiKeys.filter(key => key.envId === 1);
        const productionKeys = apiKeys.filter(key => key.envId === 6);
        return { sandboxKeys, productionKeys };
    };

    const renderKeyCard = (keys: ApiKey[], envId: number, envName: string) => {
        const activeKeys = keys.filter(key => !key.expireDate || new Date(key.expireDate) >= new Date());
        const expiringKeys = keys.filter(key => key.expireDate && new Date(key.expireDate) >= new Date());

        return (
            <Card>
                <CardHeader className={`bg-${getEnvironmentColor(envId)}-subtle`}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">
                            <Badge color={getEnvironmentColor(envId)} className="me-2">
                                {envName}
                            </Badge>
                            API Keys
                        </h5>
                        <Button
                            color={getEnvironmentColor(envId)}
                            size="sm"
                            onClick={() => handleRotateKey(envId, envName)}
                            disabled={rotating[envId]}
                        >
                            {rotating[envId] ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Rotating...
                                </>
                            ) : (
                                <>
                                    <i className="ri-refresh-line me-1"></i>
                                    Rotate Key
                                </>
                            )}
                        </Button>
                    </div>
                </CardHeader>
                <CardBody>
                    {keys.length === 0 ? (
                        <Alert color="info" className="mb-0">
                            <i className="ri-information-line me-2"></i>
                            No API keys found for this environment.
                        </Alert>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-borderless align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>API Key</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th>Expires</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {keys.map((key) => {
                                        const isExpiring: boolean = key.expireDate ? new Date(key.expireDate) >= new Date() : false;
                                        const isExpired: boolean = key.expireDate ? new Date(key.expireDate) < new Date() : false;

                                        // Safety check for apiKey
                                        if (!key.apiKey) {
                                            return null;
                                        }

                                        return (
                                            <tr key={key.id}>
                                                <td>
                                                    <code className="text-primary">
                                                        {key.apiKey.substring(0, 20)}...{key.apiKey.substring(key.apiKey.length - 8)}
                                                    </code>
                                                </td>
                                                <td>
                                                    {isExpired ? (
                                                        <Badge color="danger">Expired</Badge>
                                                    ) : isExpiring ? (
                                                        <Badge color="warning">Expiring Soon</Badge>
                                                    ) : (
                                                        <Badge color="success">Active</Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    <small className="text-muted">
                                                        {formatDate(key.createdAt)}
                                                    </small>
                                                </td>
                                                <td>
                                                    {key.expireDate ? (
                                                        <span className={isExpired ? 'text-danger' : 'text-warning'}>
                                                            <i className="ri-time-line me-1"></i>
                                                            {formatDate(key.expireDate)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">Never</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <Button
                                                        color="light"
                                                        size="sm"
                                                        onClick={() => copyToClipboard(key.apiKey)}
                                                        disabled={isExpired}
                                                    >
                                                        <i className="ri-file-copy-line"></i> Copy
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {expiringKeys.length > 0 && (
                        <Alert color="warning" className="mt-3 mb-0">
                            <i className="ri-alert-line me-2"></i>
                            <strong>Grace Period Active:</strong> You have {expiringKeys.length} key(s) that will expire soon.
                            Both old and new keys will work during the grace period.
                        </Alert>
                    )}
                </CardBody>
            </Card>
        );
    };

    const { sandboxKeys, productionKeys } = groupKeysByEnvironment();

    return (
        <React.Fragment>
            <ToastContainer closeButton={false} />
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="API Keys" pageTitle="Projects" />

                    <Row className="mb-3">
                        <Col xl={12}>
                            <Alert color="info">
                                <i className="ri-information-line me-2"></i>
                                <strong>API Keys Management:</strong> Each project has separate API keys for Sandbox and Production environments.
                                When you rotate a key, the old key remains valid for 15 days to allow smooth transition.
                            </Alert>
                        </Col>
                    </Row>

                    {loading ? (
                        <Row>
                            <Col xl={12}>
                                <Card>
                                    <CardBody className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3 text-muted">Loading API keys...</p>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    ) : (
                        <Row>
                            <Col lg={12} className="mb-3">
                                {renderKeyCard(sandboxKeys, 1, 'Sandbox')}
                            </Col>
                            <Col lg={12}>
                                {renderKeyCard(productionKeys, 6, 'Production')}
                            </Col>
                        </Row>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ProjectApiKeys;
